import React from 'react'

const AGENT_PIPELINE_ORDER = ['basecamp_data_analyzer', 'basecamp_task_creator']

export const DEFAULT_AGENT_LABELS = {
  basecamp_data_analyzer: 'Data analyzer',
  basecamp_task_creator: 'Task creator (email)',
}

/**
 * @param {string | null | undefined} outputDetail
 * @returns {Record<string, unknown> | null}
 */
export function parseRunDetailMeta(outputDetail) {
  if (!outputDetail || typeof outputDetail !== 'string') return null
  const firstLine = outputDetail.split('\n\n')[0]?.trim()
  if (!firstLine || firstLine[0] !== '{') return null
  try {
    return JSON.parse(firstLine)
  } catch {
    return null
  }
}

/**
 * @param {{ output_detail?: string | null }} row
 */
export function isDigestRun(row) {
  return parseRunDetailMeta(row.output_detail)?.digest === true
}

/**
 * Stage-preview analyzer runs from the Agents page (excludes digest pipeline rows).
 * @param {{ output_detail?: string | null }} row
 */
export function isStageAnalyzerRun(row) {
  return !isDigestRun(row)
}

/**
 * @param {string | null | undefined} start
 * @param {string | null | undefined} end
 */
export function fmtDuration(start, end) {
  if (!start || !end) return '—'
  const ms = new Date(end) - new Date(start)
  if (ms < 0) return '—'
  return ms < 1000 ? `${ms}ms` : `${Math.round(ms / 1000)}s`
}

/**
 * @param {string[]} statuses
 */
export function aggregateRunStatus(statuses) {
  if (statuses.includes('running')) return 'running'
  if (statuses.length > 0 && statuses.every((s) => s === 'skipped')) return 'skipped'
  if (statuses.includes('error')) return 'error'
  return 'success'
}

/**
 * @param {string} agentKey
 * @param {Record<string, string>} labelByKey
 */
export function agentDisplayLabel(agentKey, labelByKey = {}) {
  if (labelByKey[agentKey]) return labelByKey[agentKey]
  return DEFAULT_AGENT_LABELS[agentKey] || agentKey
}

/**
 * @param {Array<Record<string, unknown>>} rows — digest runs only
 */
export function groupDigestRuns(rows) {
  const map = new Map()
  for (const r of rows) {
    const g = r.run_group_id
    if (!g) continue
    if (!map.has(g)) {
      const meta = parseRunDetailMeta(r.output_detail)
      map.set(g, {
        run_group_id: g,
        trigger: r.trigger,
        started_at: r.created_at,
        finished_at: r.finished_at,
        statuses: [],
        links: new Set(),
        emailSent: false,
        errors: [],
        dryRun: meta?.dry_run === true,
        linkIdsFromMeta: Array.isArray(meta?.link_ids) ? meta.link_ids.length : null,
        steps: [],
      })
    }
    const grp = map.get(g)
    grp.statuses.push(r.status)
    if (r.client_basecamp_link_id) grp.links.add(r.client_basecamp_link_id)
    if (r.finished_at && r.finished_at > (grp.finished_at || '')) grp.finished_at = r.finished_at
    if (r.created_at < grp.started_at) grp.started_at = r.created_at
    if (r.agent_name === 'basecamp_task_creator' && r.status === 'success') grp.emailSent = true
    if (r.error_message) grp.errors.push(r.error_message)
    grp.steps.push({
      id: r.id,
      agent_name: r.agent_name,
      status: r.status,
      created_at: r.created_at,
      finished_at: r.finished_at,
      output_summary: r.output_summary,
      error_message: r.error_message,
      llm_model: r.llm_model,
    })
  }

  const groups = Array.from(map.values())
  for (const grp of groups) {
    grp.steps.sort((a, b) => {
      const oa = AGENT_PIPELINE_ORDER.indexOf(a.agent_name)
      const ob = AGENT_PIPELINE_ORDER.indexOf(b.agent_name)
      const ai = oa === -1 ? 99 : oa
      const bi = ob === -1 ? 99 : ob
      if (ai !== bi) return ai - bi
      return String(a.created_at).localeCompare(String(b.created_at))
    })
    grp.aggregateStatus = aggregateRunStatus(grp.statuses)
    grp.linkCount =
      grp.linkIdsFromMeta != null
        ? grp.linkIdsFromMeta
        : grp.links.size
  }
  groups.sort((a, b) => (b.started_at || '').localeCompare(a.started_at || ''))
  return groups
}

export function TriggerBadge({ trigger }) {
  const isCron = trigger === 'cron'
  const isManual = trigger === 'manual_test'
  const bg = isCron ? '#eff6ff' : isManual ? '#fffbeb' : '#f1f5f9'
  const color = isCron ? '#1d4ed8' : isManual ? '#92400e' : '#475569'
  const border = isCron ? '#bfdbfe' : isManual ? '#fde68a' : '#cbd5e1'
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        background: bg,
        color,
        border: `1px solid ${border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {isCron ? 'cron' : isManual ? 'manual test' : trigger || '—'}
    </span>
  )
}

export function StatusBadge({ status }) {
  const cfg = {
    running: { bg: '#fef9c3', color: '#854d0e', label: 'running' },
    success: { bg: '#dcfce7', color: '#15803d', label: 'success' },
    error: { bg: '#fee2e2', color: '#b91c1c', label: 'error' },
    skipped: { bg: '#f1f5f9', color: '#475569', label: 'skipped' },
  }
  const c = cfg[status] || { bg: '#f1f5f9', color: '#475569', label: status || '—' }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        background: c.bg,
        color: c.color,
        whiteSpace: 'nowrap',
      }}
    >
      {c.label}
    </span>
  )
}
